import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { AlertTriangle, ShoppingCart, Eye, RefreshCw, ExternalLink, CheckCircle2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { useArticleUnits } from '../hooks/useArticleUnits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const perms = user.permissions || {};
  return perms?.[module]?.[action] === true;
};

const priorityBadge = (p) => {
  const map = {
    urgente: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    alta: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    media: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    baja: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300',
  };
  return map[p] || map.media;
};

const procurementBadge = (ps) => {
  switch (ps) {
    case 'pending_purchase':
      return { className: 'bg-amber-50 text-amber-700 border-amber-300', icon: ShoppingCart, label: 'En compra' };
    case 'received':
      return { className: 'bg-emerald-50 text-emerald-700 border-emerald-300', icon: Truck, label: 'Stock recibido' };
    case 'externally_resolved':
      return { className: 'bg-slate-100 text-slate-700 border-slate-300', icon: ExternalLink, label: 'Atendida afuera' };
    default:
      return null;
  }
};

function UnmetDemand() {
  const { user } = useOutletContext();
  const { unitOf } = useArticleUnits();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [detailRow, setDetailRow] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // "article_id_warehouse_id"

  // "Atendida afuera" modal
  const [externResolveOpi, setExternResolveOpi] = useState(null); // {request_id, request_number, area_name}
  const [externReason, setExternReason] = useState('');
  const [externSaving, setExternSaving] = useState(false);

  // Multi-warehouse confirmation modal
  const [multiWhConfirm, setMultiWhConfirm] = useState(null); // [{warehouse_id, warehouse_name, rows: [...]}]
  const [multiWhSaving, setMultiWhSaving] = useState(false);

  const canGenerateNP = userCan(user, 'demanda_insatisfecha', 'generar_np');
  const canResolveExtern = userCan(user, 'pedidos_internos', 'rechazar');

  const fetchData = async () => {
    try {
      setLoading(true);
      const r = await axios.get(
        `${BACKEND_URL}/api/purchases/unmet-demand${includeAll ? '?include_all=true' : ''}`,
        { withCredentials: true }
      );
      setRows(r.data?.items || []);
      setSelectedKeys(new Set()); // limpiar selección
    } catch (e) {
      if (e?.response?.status === 403) toast.error('No tenés permiso para ver Demanda Insatisfecha.');
      else toast.error('Error al cargar la demanda insatisfecha');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [includeAll]);

  const rowKey = (r) => `${r.article_id}_${r.warehouse_id}`;

  const toggleRow = (r) => {
    const k = rowKey(r);
    const next = new Set(selectedKeys);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelectedKeys(next);
  };

  const toggleAll = () => {
    const selectable = rows.filter(r => r.opis.some(o => (o.procurement_status || 'none') === 'none'));
    if (selectedKeys.size === selectable.length && selectable.length > 0) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(selectable.map(rowKey)));
  };

  const selectedRows = useMemo(
    () => rows.filter(r => selectedKeys.has(rowKey(r))),
    [rows, selectedKeys]
  );

  const selectionSummary = useMemo(() => {
    if (selectedRows.length === 0) return null;
    const units = selectedRows.reduce((a, r) => a + (r.shortage_total || 0), 0);
    const opiIds = new Set(selectedRows.flatMap(r => r.opis.map(o => o.request_id)));
    const warehouses = new Map();
    for (const r of selectedRows) {
      if (!warehouses.has(r.warehouse_id)) {
        warehouses.set(r.warehouse_id, { warehouse_id: r.warehouse_id, warehouse_name: r.warehouse_name || r.warehouse_id, rows: [] });
      }
      warehouses.get(r.warehouse_id).rows.push(r);
    }
    return { articles: selectedRows.length, units, opis: opiIds.size, warehouses: Array.from(warehouses.values()) };
  }, [selectedRows]);

  const buildPrefillForWarehouse = (whGroup) => ({
    warehouse_id: whGroup.warehouse_id,
    items: whGroup.rows.map(r => ({
      article_id: r.article_id,
      article_name: r.article_name,
      quantity: r.shortage_total,
    })),
    linked_internal_request_ids: Array.from(new Set(whGroup.rows.flatMap(r => r.opis.map(o => o.request_id)))),
    from_unmet_demand: true,
  });

  const handleGenerateNP = (row) => {
    sessionStorage.setItem('np_prefill', JSON.stringify(buildPrefillForWarehouse({
      warehouse_id: row.warehouse_id,
      rows: [row],
    })));
    navigate('/purchase-requests?prefill=unmet');
  };

  const handleGenerateGrouped = () => {
    if (!selectionSummary) return;
    if (selectionSummary.warehouses.length === 1) {
      // Una sola NP via formulario (el usuario puede revisar/agregar artículos)
      sessionStorage.setItem('np_prefill', JSON.stringify(buildPrefillForWarehouse(selectionSummary.warehouses[0])));
      navigate('/purchase-requests?prefill=unmet');
    } else {
      // Multi-depósito: confirmar y crear N NPs directas
      setMultiWhConfirm(selectionSummary.warehouses);
    }
  };

  const confirmMultiWhCreate = async () => {
    if (!multiWhConfirm) return;
    setMultiWhSaving(true);
    let created = 0;
    let failed = 0;
    for (const wh of multiWhConfirm) {
      const prefill = buildPrefillForWarehouse(wh);
      const payload = {
        warehouse_id: prefill.warehouse_id,
        items: prefill.items.map(it => ({
          article_id: it.article_id,
          article_name: it.article_name,
          warehouse_id: prefill.warehouse_id,
          quantity: Number(it.quantity),
          is_new_article: false,
        })),
        priority: 'alta',
        notes: 'Generada desde Demanda Insatisfecha (agrupada multi-depósito)',
        linked_internal_request_ids: prefill.linked_internal_request_ids,
      };
      try {
        await axios.post(`${BACKEND_URL}/api/purchase-requests`, payload, { withCredentials: true });
        created += 1;
      } catch {
        failed += 1;
      }
    }
    setMultiWhSaving(false);
    setMultiWhConfirm(null);
    if (created > 0) toast.success(`${created} NP(s) generadas correctamente`);
    if (failed > 0) toast.error(`${failed} NP(s) fallaron`);
    fetchData();
    navigate('/purchase-requests');
  };

  const submitExternResolve = async () => {
    if (!externResolveOpi || !externReason.trim()) {
      toast.error('Tenés que indicar un motivo');
      return;
    }
    setExternSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/internal-requests/${externResolveOpi.request_id}/mark-externally-resolved`,
        { reason: externReason.trim() },
        { withCredentials: true }
      );
      toast.success(`OPI ${externResolveOpi.request_number} marcada como atendida afuera`);
      setExternResolveOpi(null);
      setExternReason('');
      // Refrescar lista y modal de detalle si está abierto
      await fetchData();
      if (detailRow) {
        const refreshed = rows.find(r => rowKey(r) === rowKey(detailRow));
        setDetailRow(refreshed || null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al marcar como atendida afuera');
    } finally {
      setExternSaving(false);
    }
  };

  const totalShortage = rows.reduce((acc, r) => acc + r.shortage_total, 0);
  const totalOpis = new Set(rows.flatMap(r => r.opis.map(o => o.request_id))).size;

  return (
    <div className="space-y-6" data-testid="unmet-demand-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Demanda Insatisfecha</h1>
          <p className="text-muted-foreground mt-1">
            Pedidos internos (OPI) que no se pudieron despachar por falta de stock. Generá la Nota de Pedido al exterior con un click.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Switch
              id="include-all"
              checked={includeAll}
              onCheckedChange={setIncludeAll}
              data-testid="include-all-switch"
            />
            <Label htmlFor="include-all" className="cursor-pointer">
              Incluir OPIs en proceso / resueltas
            </Label>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-button">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Artículos con faltante</CardDescription></CardHeader>
          <CardContent><p className="text-3xl font-bold">{rows.length}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Unidades faltantes</CardDescription></CardHeader>
          <CardContent><p className="text-3xl font-bold text-amber-600">{totalShortage.toLocaleString()}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>OPIs involucradas</CardDescription></CardHeader>
          <CardContent><p className="text-3xl font-bold">{totalOpis}</p></CardContent>
        </Card>
      </div>

      {/* Barra de selección múltiple */}
      {selectionSummary && canGenerateNP && (
        <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/20" data-testid="selection-bar">
          <CardContent className="py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              <strong>{selectionSummary.articles}</strong> artículo(s) seleccionado(s) ·{' '}
              <strong>{selectionSummary.units.toLocaleString()}</strong> unidades ·{' '}
              <strong>{selectionSummary.opis}</strong> OPI(s) ·{' '}
              {selectionSummary.warehouses.length === 1
                ? <span className="text-emerald-700">1 depósito → 1 NP</span>
                : <span className="text-amber-700">{selectionSummary.warehouses.length} depósitos → {selectionSummary.warehouses.length} NPs</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedKeys(new Set())} data-testid="clear-selection">
                Limpiar
              </Button>
              <Button size="sm" onClick={handleGenerateGrouped} data-testid="generate-grouped-np">
                <ShoppingCart className="w-4 h-4 mr-1" />
                {selectionSummary.warehouses.length === 1 ? 'Generar NP agrupada' : `Generar ${selectionSummary.warehouses.length} NPs`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Detalle por artículo
          </CardTitle>
          <CardDescription>
            Filtrado automáticamente por los depósitos a los que tenés acceso.
            {includeAll && ' Mostrando también OPIs ya gestionadas.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Cargando...</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="empty-state">
              🎉 No hay demanda insatisfecha pendiente. Todos los pedidos internos pueden despacharse con stock actual.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {canGenerateNP && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={rows.length > 0 && selectedKeys.size === rows.length}
                        onCheckedChange={toggleAll}
                        data-testid="select-all"
                      />
                    </TableHead>
                  )}
                  <TableHead>Depósito</TableHead>
                  <TableHead>Artículo</TableHead>
                  <TableHead className="text-right">Stock disp.</TableHead>
                  <TableHead className="text-right">Demanda</TableHead>
                  <TableHead className="text-right">Faltante</TableHead>
                  <TableHead>OPIs</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const k = rowKey(r);
                  const checked = selectedKeys.has(k);
                  // Si TODAS las OPIs de la fila ya están gestionadas (en compra,
                  // recibidas, o atendidas afuera), no tiene sentido generar otra NP.
                  const allHandled = r.opis.every(o => (o.procurement_status || 'none') !== 'none');
                  // NPs únicas asociadas (para mostrar al usuario)
                  const linkedNps = Array.from(new Set(
                    r.opis.map(o => o.linked_purchase_request_number).filter(Boolean)
                  ));
                  return (
                    <TableRow key={k} data-testid={`unmet-row-${r.article_id}`} className={checked ? 'bg-amber-50/40 dark:bg-amber-950/10' : ''}>
                      {canGenerateNP && (
                        <TableCell>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleRow(r)}
                            disabled={allHandled}
                            data-testid={`select-${r.article_id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium">{r.warehouse_name || r.warehouse_id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {r.is_medicamento && <span title="Medicamento">💊</span>}
                          {r.article_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{r.stock_available.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{r.demand_total.toLocaleString()} <span className="text-xs text-muted-foreground">{unitOf(r.article_id)}</span></TableCell>
                      <TableCell className="text-right">
                        <span className="font-bold text-red-600">{r.shortage_total.toLocaleString()}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {r.opis.slice(0, 3).map((o) => {
                            const pb = procurementBadge(o.procurement_status);
                            const shorted = !!o.shortage_note && (!o.procurement_status || o.procurement_status === 'none');
                            return (
                              <Badge
                                key={o.request_id}
                                variant="outline"
                                className={pb ? pb.className : (shorted ? 'bg-orange-100 text-orange-800 border-orange-300' : priorityBadge(o.priority))}
                                title={pb ? `${pb.label}${o.linked_purchase_request_number ? ' · ' + o.linked_purchase_request_number : ''}` : (shorted ? o.shortage_note : o.priority)}
                              >
                                {o.request_number}{shorted ? ' ⚠' : ''}
                              </Badge>
                            );
                          })}
                          {r.opis.length > 3 && <Badge variant="outline">+{r.opis.length - 3}</Badge>}
                        </div>
                        {linkedNps.length > 0 && (
                          <div className="text-xs text-amber-700 mt-1 flex items-center gap-1" title="Notas de Pedido vinculadas">
                            <ShoppingCart className="w-3 h-3" />
                            {linkedNps.join(', ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setDetailRow(r)} data-testid={`detail-${r.article_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canGenerateNP && !allHandled && (
                            <Button size="sm" onClick={() => handleGenerateNP(r)} data-testid={`gen-np-${r.article_id}`}>
                              <ShoppingCart className="w-4 h-4 mr-1" /> NP
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)}>
        <DialogContent className="max-w-3xl" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailRow?.article_name}</DialogTitle>
            <DialogDescription>
              Depósito: <strong>{detailRow?.warehouse_name}</strong> · Stock disponible: {detailRow?.stock_available} · Faltante total: <span className="text-red-600 font-bold">{detailRow?.shortage_total}</span>
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>OPI</TableHead>
                <TableHead>Área</TableHead>
                <TableHead>Prioridad</TableHead>
                <TableHead>Procurement</TableHead>
                <TableHead className="text-right">Pedido</TableHead>
                <TableHead className="text-right">Faltante</TableHead>
                <TableHead className="text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detailRow?.opis.map((o) => {
                const pb = procurementBadge(o.procurement_status);
                const Icon = pb?.icon || CheckCircle2;
                const canMarkExtern = canResolveExtern && (o.procurement_status || 'none') === 'none';
                return (
                  <TableRow key={o.request_id}>
                    <TableCell className="font-medium">{o.request_number}</TableCell>
                    <TableCell>{o.area_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={priorityBadge(o.priority)}>{o.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      {pb ? (
                        <Badge variant="outline" className={pb.className} title={o.externally_resolved_reason || o.linked_purchase_request_number || ''}>
                          <Icon className="w-3 h-3 mr-1" />{pb.label}
                          {o.linked_purchase_request_number && <span className="ml-1 text-xs opacity-70">· {o.linked_purchase_request_number}</span>}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">Sin acción</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{o.quantity_requested} <span className="text-xs text-muted-foreground">{unitOf(detailRow?.article_id)}</span></TableCell>
                    <TableCell className="text-right text-red-600">{o.shortage}</TableCell>
                    <TableCell className="text-right">
                      {canMarkExtern && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setExternResolveOpi({ request_id: o.request_id, request_number: o.request_number, area_name: o.area_name })}
                          data-testid={`mark-extern-${o.request_id}`}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" /> Atendida afuera
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <DialogFooter>
            {canGenerateNP && detailRow && (
              <Button onClick={() => { handleGenerateNP(detailRow); setDetailRow(null); }}>
                <ShoppingCart className="w-4 h-4 mr-2" /> Generar NP
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailRow(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Atendida afuera */}
      <Dialog open={!!externResolveOpi} onOpenChange={(o) => { if (!o) { setExternResolveOpi(null); setExternReason(''); } }}>
        <DialogContent data-testid="extern-resolve-dialog">
          <DialogHeader>
            <DialogTitle>Marcar OPI como atendida afuera</DialogTitle>
            <DialogDescription>
              {externResolveOpi && (
                <>
                  Vas a marcar <strong>{externResolveOpi.request_number}</strong> ({externResolveOpi.area_name}) como atendida fuera del sistema.
                  La OPI deja de aparecer en Demanda Insatisfecha pero conserva su historial para auditoría.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="extern-reason">Motivo *</Label>
            <Textarea
              id="extern-reason"
              placeholder="Ej: Compra urgente al kiosco de farmacia (recibo Nº 1234), el medicamento llegó esta mañana."
              value={externReason}
              onChange={(e) => setExternReason(e.target.value)}
              rows={4}
              data-testid="extern-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExternResolveOpi(null); setExternReason(''); }}>
              Cancelar
            </Button>
            <Button onClick={submitExternResolve} disabled={externSaving || !externReason.trim()} data-testid="confirm-extern">
              {externSaving ? 'Guardando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Multi-warehouse confirmation */}
      <Dialog open={!!multiWhConfirm} onOpenChange={(o) => !o && !multiWhSaving && setMultiWhConfirm(null)}>
        <DialogContent className="max-w-2xl" data-testid="multi-wh-dialog">
          <DialogHeader>
            <DialogTitle>Vas a generar {multiWhConfirm?.length} Notas de Pedido</DialogTitle>
            <DialogDescription>
              Los artículos seleccionados pertenecen a depósitos origen distintos, así que se crea una NP por cada depósito.
              Las NPs quedan en estado <strong>pendiente</strong> para que las edites/aprobés si necesitás cambios.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {(multiWhConfirm || []).map((wh, idx) => {
              const units = wh.rows.reduce((a, r) => a + (r.shortage_total || 0), 0);
              const opiIds = new Set(wh.rows.flatMap(r => r.opis.map(o => o.request_id)));
              return (
                <div key={wh.warehouse_id} className="border rounded-md p-3">
                  <div className="font-semibold text-sm mb-1">
                    NP #{idx + 1} → <span className="text-emerald-700">{wh.warehouse_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">
                    {wh.rows.length} artículo(s) · {units.toLocaleString()} unidades · {opiIds.size} OPI(s) linkeadas
                  </div>
                  <div className="text-xs space-y-1">
                    {wh.rows.map(r => (
                      <div key={r.article_id} className="flex justify-between">
                        <span>{r.article_name}</span>
                        <span className="font-mono text-red-600">{r.shortage_total.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMultiWhConfirm(null)} disabled={multiWhSaving}>
              Cancelar
            </Button>
            <Button onClick={confirmMultiWhCreate} disabled={multiWhSaving} data-testid="confirm-multi-wh">
              {multiWhSaving ? 'Generando...' : `Sí, generar ${multiWhConfirm?.length} NPs`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UnmetDemand;
